/**
 * Product Reviews UI Component
 * 
 * Dit bestand beheert de UI logica voor de reviews sectie.
 * Het werkt samen met de provider adapters om reviews te laden en weer te geven.
 * 
 * @typedef {import('./review-types.d.ts').NormalizedReviewData} NormalizedReviewData
 * @typedef {import('./review-types.d.ts').ReviewFetchOptions} ReviewFetchOptions
 * @typedef {import('./review-types.d.ts').ReviewUIState} ReviewUIState
 */

import { ThemeEvents } from '@theme/events';
import { getProductPageContext } from '@theme/product-page-context';
import { getProvider } from '@theme/review-provider-registry';
import { renderStarsHTML, renderStarsInElement } from '@theme/review-stars-utils';

/**
 * Yotpo ID (sitemanager) van de actieve combined-listing — staat op variant-picker na morph(main).
 * @returns {string | null}
 */
function getYotpoProductIdFromPage() {
  const picker = document.querySelector(
    'variant-picker[data-template-product-match="true"][data-yotpo-product-id], variant-picker[data-yotpo-product-id]'
  );
  const id = picker?.dataset?.yotpoProductId?.trim();
  return id || null;
}

/**
 * Volledig reviews-blok (product-reviews), niet product-review-stars (die heeft ook data-provider-name).
 * @returns {HTMLElement[]}
 */
function findReviewsWidgetSections() {
  /** @type {HTMLElement[]} */
  const sections = [];
  const seen = new Set();

  document.querySelectorAll('.reviews-list').forEach((listEl) => {
    const root = listEl.closest('[data-provider-name]');
    if (!(root instanceof HTMLElement) || seen.has(root)) return;
    seen.add(root);
    sections.push(root);
  });

  if (sections.length > 0) return sections;

  document.querySelectorAll('.product-reviews-block [data-provider-name]').forEach((el) => {
    if (el instanceof HTMLElement && !seen.has(el)) {
      seen.add(el);
      sections.push(el);
    }
  });

  return sections;
}

/**
 * @returns {HTMLElement | null}
 */
function findPrimaryReviewsSection() {
  const sections = findReviewsWidgetSections();
  return sections.length > 0 ? sections[0] : null;
}

/**
 * Zorg dat reviews.css actief blijft na morph(main) (link in main wordt verwijderd).
 * @param {string} [cssUrl] — fallback uit data-reviews-css-url op het reviews-blok
 */
function ensureReviewsStylesheet(cssUrl) {
  const headLinks = document.head.querySelectorAll('link[rel="stylesheet"]');
  for (const node of headLinks) {
    if (!(node instanceof HTMLLinkElement)) continue;
    if (!node.href.includes('reviews.css')) continue;
    node.media = 'all';
    return;
  }

  const bodyLinks = document.querySelectorAll('link[rel="stylesheet"][href*="reviews.css"]');
  for (const node of bodyLinks) {
    if (!(node instanceof HTMLLinkElement)) continue;
    node.media = 'all';
    document.head.appendChild(node);
    return;
  }

  const href =
    cssUrl?.trim() ||
    findPrimaryReviewsSection()?.dataset?.reviewsCssUrl?.trim();
  if (!href) return;

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  link.media = 'all';
  document.head.appendChild(link);
}

/**
 * Reviews UI Component Class
 */
class ReviewsComponent {
  /** @type {number} */
  #reloadGeneration = 0;

  /** @type {boolean} */
  #morphReloadScheduled = false;

  /** @type {string | null} */
  #pendingMorphTrigger = null;

  /** @type {boolean} */
  #initializing = true;

  constructor() {
    this.section = null;
    this.provider = null;
    this.state = {
      currentPage: 1,
      currentSort: 'newest',
      photoFilterActive: false,
      isLoading: false,
      hasError: false,
      errorMessage: null
    };
    
    // Cache alle opgehaalde reviews voor paginatie
    this.allReviews = [];
    this.userPerPage = 10;
    this.paginationData = null;
    
    // Dialog state
    this.dialogState = {
      isOpen: false,
      currentPage: 1,
      isLoading: false,
      hasMore: true,
      infiniteScrollActive: false
    };

    /** @type {boolean} */
    this._reviewsListenersAttached = false;

    this.init().finally(() => {
      this.#initializing = false;
    });

    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdateForMorph);
    document.addEventListener('hds:main-morphed', this.#onMainMorphForReviews);

    window.__hdsReviewsReloadAfterMorph = (trigger) => {
      this.#scheduleMorphReload(trigger || 'manual');
    };

  }

  /**
   * @param {string} message
   * @param {unknown} [data]
   */
  #reviewsMorphLog(message, data) {
    if (!this.isDebugMode()) return;
    if (data !== undefined) {
      console.info('[hds-reviews]', message, data);
    } else {
      console.info('[hds-reviews]', message);
    }
  }

  /**
   * @param {Event} event
   * @returns {boolean}
   */
  /**
   * Alleen combined listing (ander child-product), niet elke variantwissel —
   * variant-picker stuurt bij elke wijziging een html Document mee.
   * @param {Event} event
   * @returns {boolean}
   */
  #shouldReloadAfterVariantEvent(event) {
    if (!(event instanceof CustomEvent)) return false;
    const newProduct = event.detail?.data?.newProduct;
    return newProduct != null && typeof newProduct === 'object';
  }

  /**
   * @param {Event} event
   */
  #onVariantUpdateForMorph = (event) => {
    if (this.#initializing) return;

    const shouldReload = this.#shouldReloadAfterVariantEvent(event);

    this.#reviewsMorphLog('variant:update', {
      shouldReload,
      newProduct: event.detail?.data?.newProduct ?? null,
    });

    if (!shouldReload) return;
    this.#scheduleMorphReload('variant:update');
  };

  #onMainMorphForReviews = () => {
    if (this.#initializing) return;
    this.#scheduleMorphReload('hds:main-morphed');
  };

  /**
   * @param {string} trigger
   */
  #scheduleMorphReload(trigger) {
    this.#pendingMorphTrigger = this.#pendingMorphTrigger
      ? `${this.#pendingMorphTrigger}+${trigger}`
      : trigger;

    if (this.#morphReloadScheduled) return;
    this.#morphReloadScheduled = true;

    queueMicrotask(() => {
      requestAnimationFrame(() => {
        this.#morphReloadScheduled = false;
        const t = this.#pendingMorphTrigger || trigger;
        this.#pendingMorphTrigger = null;
        this.#reloadReviewsAfterMorph(t);
      });
    });
  }

  /**
   * Combined listing / morph(main): sectie in DOM is vervangen; data opnieuw laden.
   * @param {string} trigger
   */
  async #reloadReviewsAfterMorph(trigger) {
    const generation = ++this.#reloadGeneration;

    ensureReviewsStylesheet();

    const newSection = findPrimaryReviewsSection();
    if (!(newSection instanceof HTMLElement)) {
      this.#reviewsMorphLog('Geen reviews-widget (.reviews-list) in DOM', { trigger });
      return;
    }

    const sectionReplaced = !this.section || this.section !== newSection;

    this.#reviewsMorphLog('reload start', {
      trigger,
      generation,
      sectionReplaced,
      productIdFromPicker: document.querySelector('variant-picker[data-product-id]')
        ?.dataset?.productId,
    });

    this.section = newSection;
    this.starColor = this.section.dataset.starColor || '#b8915c';
    this.starEmptyColor = this.section.dataset.starEmptyColor || '#d1d1d1';

    this.allReviews = [];
    this.state.currentPage = 1;
    this.state.currentSort = 'newest';
    this.state.photoFilterActive = false;
    this.state.isLoading = false;
    this.state.hasError = false;
    this.state.errorMessage = null;
    this.paginationData = null;
    this.dialogState = {
      isOpen: false,
      currentPage: 1,
      isLoading: false,
      hasMore: true,
      infiniteScrollActive: false,
    };

    this.#hideWidgetChromeForReload();
    this.#setWidgetLoadingVisible(true);

    try {
      if (generation !== this.#reloadGeneration) return;

      await this.loadProvider();

      if (generation !== this.#reloadGeneration) return;

      let productId = await this.resolveProductId();
      if (!productId) {
        await new Promise((resolve) => setTimeout(resolve, 300));
        productId = await this.resolveProductId();
      }
      if (!productId) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        productId = await this.resolveProductId();
      }

      if (generation !== this.#reloadGeneration) return;

      if (!productId) {
        this.#reviewsMorphLog('Geen productId na morph', { trigger });
        this.#showEmptyReviewsState();
        return;
      }

      this.#reviewsMorphLog('productId resolved', { productId, trigger });

      if (sectionReplaced || !this._reviewsListenersAttached) {
        this.setupEventListeners();
        this._reviewsListenersAttached = true;
      }

      await this.loadInitialData(productId, false);

      if (generation !== this.#reloadGeneration) return;

      this.#syncDialogChromeFromWidget();
      this.#reviewsMorphLog('reload klaar', { trigger, productId });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.#reviewsMorphLog('reload fout', { trigger, message });
      this.showError(message);
    } finally {
      this.#setWidgetLoadingVisible(false);
    }
  }

  /**
   * Hoofd-widget (niet de mobiele dialog) — voorkomt dubbele summary/breakdown in queries.
   * @returns {HTMLElement | null}
   */
  #getWidgetRoot() {
    return this.section?.querySelector('.reviews-widget') ?? null;
  }

  /**
   * @param {string} selector
   * @returns {HTMLElement[]}
   */
  #queryWidgetAll(selector) {
    const widget = this.#getWidgetRoot();
    if (!widget) return [];
    return Array.from(widget.querySelectorAll(selector)).filter(
      (el) => el instanceof HTMLElement
    );
  }

  /**
   * @param {string} selector
   * @returns {HTMLElement | null}
   */
  #queryWidgetOne(selector) {
    const widget = this.#getWidgetRoot();
    if (!widget) return null;
    const el = widget.querySelector(selector);
    return el instanceof HTMLElement ? el : null;
  }

  /**
   * Alleen de loading-regel in de hoofd-widget (geen CSS !important).
   * @param {boolean} visible
   */
  #setWidgetLoadingVisible(visible) {
    const loadingEl = this.#queryWidgetOne('.reviews-list__loading');
    if (!loadingEl) return;

    if (visible) {
      loadingEl.hidden = false;
      loadingEl.style.display = 'block';
      loadingEl.removeAttribute('aria-hidden');
    } else {
      loadingEl.hidden = true;
      loadingEl.style.display = 'none';
      loadingEl.setAttribute('aria-hidden', 'true');
    }
  }

  /**
   * Voor combined listing: verberg oude chrome/content tot nieuwe data binnen is.
   */
  #hideWidgetChromeForReload() {
    const widget = this.#getWidgetRoot();
    if (!widget) return;

    this.hideError();

    widget.querySelectorAll('.reviews-summary, .reviews-breakdown').forEach((el) => {
      el.hidden = true;
      el.setAttribute('aria-busy', 'true');
    });

    const contentEl = this.#queryWidgetOne('.reviews-list__content');
    const emptyEl = this.#queryWidgetOne('.reviews-list__empty');
    if (contentEl) {
      contentEl.innerHTML = '';
      contentEl.hidden = true;
    }
    if (emptyEl) emptyEl.hidden = true;
  }

  /**
   * Dialog-summary gelijk trekken met de hoofd-widget (mobiel).
   */
  #syncDialogChromeFromWidget() {
    const widget = this.#getWidgetRoot();
    const dialog = this.section?.querySelector('.reviews-dialog');
    if (!widget || !dialog) return;

    const pairs = [
      ['.reviews-summary__number', 'textContent'],
      ['.reviews-summary__count', 'textContent'],
    ];

    pairs.forEach(([sel, prop]) => {
      const src = widget.querySelector(sel);
      const dst = dialog.querySelector(sel);
      if (src && dst) dst[prop] = src[prop];
    });

    const wStars = widget.querySelector('.reviews-summary__stars');
    const dStars = dialog.querySelector('.reviews-summary__stars');
    if (wStars && dStars) {
      dStars.innerHTML = wStars.innerHTML;
    }

    const wSummary = widget.querySelector('.reviews-summary');
    const dSummary = dialog.querySelector('.reviews-summary');
    if (wSummary instanceof HTMLElement && dSummary instanceof HTMLElement) {
      dSummary.hidden = wSummary.hidden;
    }

    const wBreakdown = widget.querySelector('.reviews-breakdown');
    const dBreakdown = dialog.querySelector('.reviews-breakdown');
    if (wBreakdown && dBreakdown) {
      dBreakdown.hidden = wBreakdown.hidden;
      dBreakdown.querySelectorAll('.reviews-breakdown__fill').forEach((fill, i) => {
        const srcFill = wBreakdown.querySelectorAll('.reviews-breakdown__fill')[i];
        if (fill instanceof HTMLElement && srcFill instanceof HTMLElement) {
          fill.style.width = srcFill.style.width;
        }
      });
      dBreakdown.querySelectorAll('.reviews-breakdown__percentage').forEach((pct, i) => {
        const srcPct = wBreakdown.querySelectorAll('.reviews-breakdown__percentage')[i];
        if (pct instanceof HTMLElement && srcPct instanceof HTMLElement) {
          pct.textContent = srcPct.textContent;
        }
      });
    }
  }

  /**
   * Verberg summary/breakdown; toon alleen lege-staattekst in de widget.
   */
  #showEmptyReviewsState() {
    const widget = this.#getWidgetRoot();
    if (!widget) return;

    this.hideError();

    widget.querySelectorAll('.reviews-summary, .reviews-breakdown').forEach((el) => {
      el.hidden = true;
      el.removeAttribute('aria-busy');
    });

    const contentEl = widget.querySelector('.reviews-list__content');
    const emptyEl = widget.querySelector('.reviews-list__empty');
    const loadingEl = widget.querySelector('.reviews-list__loading');

    if (contentEl) {
      contentEl.innerHTML = '';
      contentEl.hidden = true;
    }
    if (emptyEl) emptyEl.hidden = false;
    if (loadingEl) {
      loadingEl.hidden = true;
      loadingEl.style.display = 'none';
      loadingEl.setAttribute('aria-hidden', 'true');
    }

    widget
      .querySelectorAll('.reviews-widget__show-all')
      .forEach((btn) => {
        if (btn instanceof HTMLElement) btn.style.display = 'none';
      });

    this.#setWidgetLoadingVisible(false);
  }

  /**
   * Initialiseert de component
   */
  async init() {
    const sections = findReviewsWidgetSections();

    this.#reviewsMorphLog('init', {
      widgetSectionCount: sections.length,
      providerNameNodes: document.querySelectorAll('[data-provider-name]').length,
    });

    if (sections.length === 0) {
      return;
    }

    for (const section of sections) {
      await this.initSection(section);
    }
  }
  
  /**
   * Initialiseert een specifieke reviews sectie
   * @param {HTMLElement} section - De reviews sectie element
   */
  async initSection(section) {
    this.section = section;

    ensureReviewsStylesheet(this.section.dataset.reviewsCssUrl);

    // Lees kleuren uit data-attributen
    this.starColor = this.section.dataset.starColor || '#b8915c';
    this.starEmptyColor = this.section.dataset.starEmptyColor || '#d1d1d1';

    this.setLoading(true);

    try {
      // Laad provider
      await this.loadProvider();
      
      // Resolve product ID met retry mechanisme
      let productId = await this.resolveProductId();
      
      // Als geen product ID gevonden, wacht even en probeer opnieuw
      if (!productId) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        productId = await this.resolveProductId();
      }
      
      // Als nog steeds geen product ID, probeer URL-based fallback
      if (!productId) {
        const urlMatch = window.location.pathname.match(/\/products\/([^\/\?]+)/);
        if (urlMatch) {
          productId = urlMatch[1];

        }
      }
      
      if (!productId) {
        this.#showEmptyReviewsState();
        return;
      }

      // Setup event listeners
      this.setupEventListeners();
      this._reviewsListenersAttached = true;

      // Laad initial data
      await this.loadInitialData(productId);

      this.#reviewsMorphLog('initSection klaar', { productId });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.#reviewsMorphLog('initSection fout', { message });
      this.showError(message);
    } finally {
      if (this.section?.querySelector('.reviews-list')) {
        this.setLoading(false);
      }
    }
  }
  
  /**
   * Laadt de juiste provider adapter
   */
  async loadProvider() {
    const providerName = this.section.dataset.providerName;
    
    if (!providerName) {
      throw new Error('Provider naam niet gevonden in data-provider-name');
    }
    
    this.provider = await getProvider(providerName);
    
    // Maak een wrapper object voor provider-specifieke configuratie
    this.providerConfig = {
      ...this.provider,
      providerName: providerName
    };
    
    // Voeg provider-specifieke configuratie toe
    if (providerName === 'yotpo') {
      const appKey = this.section.dataset.yotpoAppKey;
      if (!appKey) {
        throw new Error('Yotpo App Key is vereist');
      }
      this.providerConfig.appKey = appKey;
    }
    
    if (this.isDebugMode()) {
      console.info(`[Reviews] Provider geladen: ${providerName}`);
    }
  }
  
  /**
   * Resolve product ID op basis van de configuratie
   * @returns {Promise<string|null>} Product ID of null als niet gevonden
   */
  async resolveProductId() {
    const mode = this.section.dataset.productIdMode || 'auto';
    const customId = this.section.dataset.productIdCustom;
  
    
    // Probeer verschillende manieren om product data te krijgen
    const productData = this.getProductData();
    
    if (mode === 'custom') {
      const liveYotpoId = getYotpoProductIdFromPage();
      if (liveYotpoId && this.section) {
        this.section.dataset.productIdCustom = liveYotpoId;
      }

      const candidates = [
        liveYotpoId,
        customId,
        productData?.id?.toString(),
        productData?.handle,
      ].filter((id, index, list) => id && list.indexOf(id) === index);

      for (const candidate of candidates) {
        try {
          if (await this.testProductId(candidate)) return candidate;
        } catch {
          /* volgende kandidaat */
        }
      }

      const fallback = liveYotpoId || customId;
      return fallback || null;
    }

    if (mode === 'shopify_id') {
      const shopifyId = productData?.id?.toString() || null;
      return shopifyId;
    }
    
    if (mode === 'handle') {
      const handle = productData?.handle || null;
      return handle;
    }
    
    // Auto mode: probeer in volgorde
    const candidates = [
      customId,
      productData?.id?.toString(),
      productData?.handle
    ].filter(Boolean);
    
    // Test elk candidate tot we er een vinden die werkt
    for (const candidate of candidates) {
      try {
        const isValid = await this.testProductId(candidate);

        if (isValid) {

          return candidate;
        }
      } catch (error) {
        console.info(`[Reviews] ❌ Product ID ${candidate} invalid:`, error.message);
      }
    }
    

    return null;
  }
  
  /**
   * Probeer product data te krijgen uit verschillende bronnen
   * @returns {Object|null} Product data object
   */
  getProductData() {
    const live = getProductPageContext();
    if (live) return live;

    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (productForm) {
      const productIdInput = productForm.querySelector('input[name="id"]');
      if (productIdInput instanceof HTMLInputElement && productIdInput.value) {
        return {
          id: productIdInput.value,
        };
      }
    }

    return null;
  }
  
  /**
   * Test of een product ID geldig is
   * @param {string} productId - Product ID om te testen
   * @returns {Promise<boolean>} True als geldig
   */
  async testProductId(productId) {
    if (!this.providerConfig?.testProductId) {
      // Als provider geen test functie heeft, probeer summary te laden
      const options = this.buildFetchOptions(productId, 1, 1);
      
      try {
        await this.providerConfig.fetchSummary(options);
        return true;
      } catch (error) {
        return false;
      }
    }
    
    try {
      const result = await this.providerConfig.testProductId(productId, this.providerConfig.appKey);
      return result;
    } catch (error) {
      return false;
    }
  }
  
  /**
   * Setup event listeners voor UI controls
   */
  setupEventListeners() {
    // Sort dropdown
    const sortSelect = this.section.querySelector('.reviews-controls__sort-select');
    if (sortSelect) {
      sortSelect.addEventListener('change', (e) => {
        this.state.currentSort = e.target.value;
        this.state.currentPage = 1;
        this.loadReviews();
      });
    }
    
    // Per page dropdown
    const perPageSelect = this.section.querySelector('.reviews-controls__per-page-select');
    if (perPageSelect) {
      perPageSelect.addEventListener('change', (e) => {
        this.state.currentPage = 1;
        this.loadReviews();
      });
    }
    
    // Photo filter checkbox
    const photoFilter = this.section.querySelector('.reviews-controls__photo-filter');
    if (photoFilter) {
      photoFilter.addEventListener('change', (e) => {
        this.state.photoFilterActive = e.target.checked;
        this.state.currentPage = 1;
        this.loadReviews();
      });
    }
    
    // Pagination buttons
    const prevBtn = this.section.querySelector('.reviews-pagination__prev');
    const nextBtn = this.section.querySelector('.reviews-pagination__next');
    
    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        if (this.state.currentPage > 1) {
          this.state.currentPage--;
          this.loadReviews();
        }
      });
    }
    
    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        this.state.currentPage++;
        this.loadReviews();
      });
    }
    
    // Retry button
    const retryBtn = this.section.querySelector('.reviews-list__retry-btn');
    if (retryBtn) {
      retryBtn.addEventListener('click', () => {
        this.loadReviews();
      });
    }
    
    // Show more reviews button - beide desktop en mobile versies
    const showMoreBtns = this.section.querySelectorAll('.reviews-widget__show-all');
    showMoreBtns.forEach(showMoreBtn => {
      if (showMoreBtn) {
        showMoreBtn.addEventListener('click', (e) => {
          e.preventDefault();
          
          // Op mobiel: open dialog, op desktop: laad meer reviews zoals nu
          if (this.isMobile()) {
            this.openReviewsDialog();
          } else {
            this.loadMoreReviews();
          }
        });
      }
    });
    
    // Lees meer knoppen voor individuele reviews en replies (event delegation)
    const reviewsContainer = this.#queryWidgetOne('.reviews-list');
    if (reviewsContainer) {
      reviewsContainer.addEventListener('click', (e) => {
        if (e.target && e.target.classList && 
            (e.target.classList.contains('review-item__read-more') || 
             e.target.classList.contains('review-reply__read-more'))) {
          e.preventDefault();
          this.toggleReviewContent(e.target);
        }
      });
    }
  }
  
  /**
   * Laadt initial data (alleen reviews, bevat ook summary)
   * @param {string} productId - Product ID
   */
  /**
   * @param {string} productId
   * @param {boolean} [manageLoading=true] false bij morph-reload (loading al door parent)
   */
  async loadInitialData(productId, manageLoading = true) {
    if (manageLoading) {
      this.setLoading(true);
    }

    try {
      await this.loadReviews(productId, true);
    } catch (error) {
      this.showError(error.message);
    } finally {
      if (manageLoading) {
        this.setLoading(false);
      }
    }
  }
  
  /**
   * Laadt reviews data
   * @param {string} productId - Product ID (optioneel, gebruikt huidige als niet gegeven)
   * @param {boolean} skipLoadingState - Skip loading state management (voor loadInitialData)
   */
  async loadReviews(productId = null, skipLoadingState = false) {
    if (!productId) {
      productId = await this.resolveProductId();
    }
    
    if (!productId) {
      this.showError('Product ID niet beschikbaar');
      return;
    }
    
    if (!skipLoadingState) {
      this.setLoading(true);
      this.hideError();
    }
    
    try {
      let perPage = this.getPerPageValue();
      const userPerPage = perPage; // Onthoud de originele perPage van de gebruiker
      
      // Voor de eerste load, haal maximaal 150 reviews op voor een accurate breakdown
      // Breek deze dan handmatig op in paginas van de userPerPage size
      if (this.state.currentPage === 1 && this.getTotalLoadedReviews() === 0) {
        perPage = 150; // Haal maximaal 150 reviews op
      }
      
      const options = this.buildFetchOptions(productId, perPage, this.state.currentPage);
      
      const data = await this.providerConfig.fetchReviews(options);
      
      // Check of er breakdown data is van de API
      const hasBreakdownFromAPI = !!(data.summary && data.summary.breakdown);
      
      // Als er geen breakdown data is van de API, bereken uit alle reviews in de response
      if (!hasBreakdownFromAPI && data.reviews && data.reviews.length > 0) {
        const starCounts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
        let totalReviews = 0;
        
        data.reviews.forEach(review => {
          const score = review.score;
          if (score >= 1 && score <= 5) {
            starCounts[score]++;
            totalReviews++;
          }
        });
        
        const average =
          totalReviews > 0
            ? data.reviews.reduce((sum, r) => sum + (r.score || 0), 0) /
              totalReviews
            : 0;

        if (!data.summary) {
          data.summary = {
            average,
            count: data.pagination?.total || totalReviews,
          };
        } else {
          if (!data.summary.count) {
            data.summary.count = data.pagination?.total || totalReviews;
          }
          if (!data.summary.average && average > 0) {
            data.summary.average = average;
          }
        }
        data.summary.breakdown = {
          totalReviews: totalReviews,
          starDistribution: starCounts,
        };
      }
      
      // Als we meer reviews hebben opgehaald dan userPerPage, split deze op
      if (perPage === 150 && data.reviews && data.reviews.length > userPerPage) {
        // Maak een DEEP COPY van de data om de provider cache niet te modificeren
        const allReviewsCopy = JSON.parse(JSON.stringify(data.reviews));
        
        // Bewaar alle reviews in de component state (VOOR we slices maken!)
        this.allReviews = allReviewsCopy;
        this.userPerPage = userPerPage;
        this.paginationData = data.pagination;
        
        // Update pagination info om te weerspiegelen dat we meerdere pages hebben
        const totalPages = Math.ceil(data.reviews.length / userPerPage);
        data.pagination = {
          page: 1,
          perPage: userPerPage,
          total: data.reviews.length
        };
        
        // Show only first page - maak nieuwe array voor display
        data.reviews = [...data.reviews].slice(0, userPerPage);
      } else if (perPage === 150 && data.reviews && data.reviews.length <= userPerPage) {
        // Er zijn minder reviews dan we wilden ophalen - dit betekent dat we alle reviews hebben
        // Bewaar ze toch als allReviews voor consistentie
        this.allReviews = [...data.reviews];
        this.userPerPage = userPerPage;
      }
      
      const reviewItems = data.reviews?.length ?? 0;

      if (reviewItems === 0) {
        if (this.isDebugMode()) {
          console.info('[hds-reviews] lege staat', {
            productId,
            summaryCount: data.summary?.count,
            paginationTotal: data.pagination?.total,
          });
        }
        this.#showEmptyReviewsState();
        return;
      }

      if (data.summary && !data.summary.count) {
        data.summary.count = data.pagination?.total || reviewItems;
      }

      this.renderReviews(data, hasBreakdownFromAPI);
      this.renderPagination(data.pagination);

      if (data.summary) {
        this.renderSummary(data.summary);
        if (data.summary.breakdown) {
          this.renderBreakdown(data.summary.breakdown);
        }
      }
      
    } catch (error) {
      this.showError(error.message);
    } finally {
      if (!skipLoadingState) {
        this.setLoading(false);
      }
    }
  }
  
  /**
   * Laadt meer reviews (append mode)
   * @param {string} productId - Product ID (optioneel, gebruikt huidige als niet gegeven)
   */
  async loadMoreReviews(productId = null) {
    if (!productId) {
      productId = await this.resolveProductId();
    }
    
    if (!productId) {
      this.showError('Product ID niet beschikbaar');
      return;
    }
    
    const showMoreBtns = this.section.querySelectorAll('.reviews-widget__show-all');
    showMoreBtns.forEach(btn => {
      if (btn) {
        btn.style.pointerEvents = 'none';
        btn.setAttribute('aria-busy', 'true');
      }
    });
    
    try {
      const perPage = this.getPerPageValue();
      const nextPage = this.state.currentPage + 1;
      
      // Check of we gecached reviews hebben voor handmatige paginatie
      if (this.allReviews && this.allReviews.length > 0) {
        const startIdx = (nextPage - 1) * perPage;
        const endIdx = startIdx + perPage;
        const reviewsToShow = this.allReviews.slice(startIdx, endIdx);
        
        if (reviewsToShow.length === 0) {
          // Geen meer reviews beschikbaar
          this.updateShowAllButtons(this.allReviews.length);
          
          const showMoreBtns = this.section.querySelectorAll('.reviews-widget__show-all');
          showMoreBtns.forEach(btn => {
            if (btn) {
              btn.setAttribute('aria-busy', 'false');
              btn.style.pointerEvents = 'auto';
            }
          });
          
          return;
        }
        
        // Append reviews aan bestaande lijst
        this.appendReviews(reviewsToShow, null, productId);
        
        // Update pagination state
        this.state.currentPage = nextPage;
        
        // Update pagination display
        const totalPages = Math.ceil(this.allReviews.length / perPage);
        this.renderPagination({
          page: nextPage,
          perPage: perPage,
          total: this.allReviews.length
        });
        
        // Update button state
        this.updateShowAllButtons(this.allReviews.length);
        
        // Re-enable buttons
        const showMoreBtns = this.section.querySelectorAll('.reviews-widget__show-all');
        showMoreBtns.forEach(btn => {
          if (btn) {
            btn.setAttribute('aria-busy', 'false');
            btn.style.pointerEvents = 'auto';
          }
        });
        
        return;
      }
      
      // Fallback naar normale API call als er geen cached reviews zijn
      const options = this.buildFetchOptions(productId, perPage, nextPage);
      
      const data = await this.providerConfig.fetchReviews(options);
      
      if (data.reviews.length === 0) {
        // Geen meer reviews beschikbaar - update show all buttons
        this.updateShowAllButtons(data.pagination.total);
        return;
      }
      
      // Append nieuwe reviews aan bestaande lijst
      this.appendReviews(data.reviews, data.groupingData || null, data.currentProductId || null);
      
      // Update pagination state
      this.state.currentPage = nextPage;
      
      // Update breakdown alleen als er al breakdown data bestaat
      // Anders gebruik de API breakdown data die al in de state zit
      const currentBreakdownEls = this.section.querySelectorAll('.reviews-breakdown');
      const hasBreakdownData = currentBreakdownEls.length > 0 && 
        currentBreakdownEls[0].querySelector('.reviews-breakdown__fill[style*="width"]');
      
      if (!hasBreakdownData) {
        // Alleen bereken als er nog geen breakdown data is
        this.calculateAndRenderBreakdownFromReviews();
      }
      
      // Update show more buttons - beide desktop en mobile versies
      this.updateShowAllButtons(data.pagination.total);
      
    } catch (error) {
      const showMoreBtns = this.section.querySelectorAll('.reviews-widget__show-all');
      showMoreBtns.forEach(btn => {
        if (btn) {
          btn.setAttribute('aria-busy', 'false');
          btn.style.pointerEvents = 'auto';
        }
      });
      this.showError(error.message);
    }
  }
  
  /**
   * Haalt de huidige per_page waarde op
   * Eerst kijkt het naar het data-per-page attribuut, dan naar de dropdown, dan naar default 10
   * @returns {number} Items per pagina
   */
  getPerPageValue() {
    // Methode 1: Kijk naar data-per-page attribuut (van block settings)
    const dataPerPage = this.section.getAttribute('data-per-page');
    if (dataPerPage) {
      const parsed = parseInt(dataPerPage);
      if (!isNaN(parsed)) {
        return parsed;
      }
    }
    
    // Methode 2: Kijk naar de dropdown waarde
    const perPageSelect = this.section.querySelector('.reviews-controls__per-page-select');
    if (perPageSelect) {
      const parsed = parseInt(perPageSelect.value);
      if (!isNaN(parsed)) {
        return parsed;
      }
    }
    
    // Methode 3: Default naar 10
    return 10;
  }
  
  /**
   * Bouwt fetch options object
   * @param {string} productId - Product ID
   * @param {number} perPage - Items per pagina
   * @param {number} page - Pagina nummer
   * @returns {ReviewFetchOptions} Fetch options
   */
  buildFetchOptions(productId, perPage, page) {
    const options = {
      productId,
      perPage,
      page,
      sort: this.state.currentSort,
      direction: 'desc', // Wordt door provider bepaald op basis van sort
      pictured: this.state.photoFilterActive,
      debug: this.isDebugMode(),
      locale: document.documentElement.lang || 'nl',
      appKey: this.providerConfig.appKey // Provider-specifieke configuratie
    };
    
    return options;
  }
  
  /**
   * Toont of verbergt summary en sterrenverdeling op basis van review-aantal
   * @param {number} reviewCount - Totaal aantal reviews
   */
  setSummaryBreakdownVisibility(reviewCount) {
    const hasReviews = reviewCount > 0;

    this.#queryWidgetAll('.reviews-summary, .reviews-breakdown').forEach((el) => {
      el.hidden = !hasReviews;
      if (hasReviews) {
        el.removeAttribute('aria-busy');
      }
    });
  }

  /**
   * Rendert summary data
   * @param {Object} summary - Summary data
   */
  renderSummary(summary) {
    const summaryEls = this.#queryWidgetAll('.reviews-summary');
    const averageEls = this.#queryWidgetAll('.reviews-summary__number');
    const countEls = this.#queryWidgetAll('.reviews-summary__count');

    const average = summary.average || 0;
    const count = summary.count || 0;

    this.setSummaryBreakdownVisibility(count);

    averageEls.forEach((el) => {
      el.textContent = average.toFixed(1);
    });

    countEls.forEach((el) => {
      const countText = count === 1 ? 'review' : 'reviews';
      el.textContent = `Gebaseerd op ${count} ${countText}`;
    });

    this.renderStars(average);
    this.updateShowAllButtons(count);

    summaryEls.forEach((el) => {
      el.setAttribute('aria-busy', 'false');
    });

    this.#syncDialogChromeFromWidget();
  }
  
  /**
   * Rendert sterren display
   * @param {number} average - Gemiddelde score
   */
  renderStars(average) {
    this.#queryWidgetAll('.reviews-summary__stars').forEach((el) => {
      renderStarsInElement(el, average, this.starColor, this.starEmptyColor);
    });
  }
  
  
  /**
   * Rendert breakdown (sterrenverdeling)
   * @param {Object} breakdown - Breakdown data met sterrenverdeling
   */
  renderBreakdown(breakdown) {
    const breakdownEls = this.#queryWidgetAll('.reviews-breakdown');
    
    if (!breakdownEls || breakdownEls.length === 0) return;

    const totalReviews = breakdown?.totalReviews ?? 0;
    if (totalReviews === 0) {
      breakdownEls.forEach((el) => {
        el.hidden = true;
      });
      return;
    }
    
    // Als er geen breakdown data is, bereken het op basis van reviews
    if (!breakdown || !breakdown.starDistribution) {
      this.calculateAndRenderBreakdownFromReviews();
      return;
    }
    const starDistribution = breakdown.starDistribution;
    
    // Update elke sterrenverdeling item in elke breakdown element
    breakdownEls.forEach(breakdownEl => {
      for (let star = 5; star >= 1; star--) {
        const count = starDistribution[star] || 0;
        const percentage = totalReviews > 0 ? Math.round((count / totalReviews) * 100) : 0;
        
        const itemEl = breakdownEl.querySelector(`.reviews-breakdown__item:nth-child(${6 - star})`);
        
        if (itemEl) {
          const fillEl = itemEl.querySelector('.reviews-breakdown__fill');
          const percentageEl = itemEl.querySelector('.reviews-breakdown__percentage');
          
          if (fillEl) {
            fillEl.style.width = `${percentage}%`;
          }
          
          if (percentageEl) {
            percentageEl.textContent = `${percentage}%`;
          }
        }
      }
    });

    this.#syncDialogChromeFromWidget();
  }
  
  /**
   * Berekent en rendert breakdown op basis van geladen reviews
   */
  calculateAndRenderBreakdownFromReviews() {
    const reviewsEl = this.#queryWidgetOne('.reviews-list__content');
    if (!reviewsEl) return;
    
    const reviewItems = reviewsEl.querySelectorAll('.review-item');
    if (reviewItems.length === 0) return;
    
    // Tel sterrenverdeling
    const starCounts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    let totalReviews = 0;
    
    reviewItems.forEach(item => {
      const starsEl = item.querySelector('.review-item__stars');
      if (starsEl) {
        const stars = starsEl.textContent.match(/★/g);
        if (stars) {
          const score = stars.length;
          if (score >= 1 && score <= 5) {
            starCounts[score]++;
            totalReviews++;
          }
        }
      }
    });
    
    if (totalReviews === 0) return;
    
    const breakdownEls = this.#queryWidgetAll('.reviews-breakdown');
    if (!breakdownEls.length) return;
    
    breakdownEls.forEach(breakdownEl => {
      for (let star = 5; star >= 1; star--) {
        const count = starCounts[star];
        const percentage = Math.round((count / totalReviews) * 100);
        
        const itemEl = breakdownEl.querySelector(`.reviews-breakdown__item:nth-child(${6 - star})`);
        if (itemEl) {
          const fillEl = itemEl.querySelector('.reviews-breakdown__fill');
          const percentageEl = itemEl.querySelector('.reviews-breakdown__percentage');
          
          if (fillEl) {
            fillEl.style.width = `${percentage}%`;
          }
          
          if (percentageEl) {
            percentageEl.textContent = `${percentage}%`;
          }
        }
      }
    });

    this.#syncDialogChromeFromWidget();
  }
  
  /**
   * Voegt nieuwe reviews toe aan de bestaande lijst
   * @param {Array} newReviews - Nieuwe reviews om toe te voegen
   * @param {Object} groupingData - Grouping data voor variant informatie
   * @param {string} currentProductId - Huidige product ID
   * @param {HTMLElement} targetContainer - Optionele target container (default: hoofdpagina)
   */
  appendReviews(newReviews, groupingData = null, currentProductId = null, targetContainer = null) {
    // Gebruik target container of fallback naar hoofdpagina
    const contentEl =
      targetContainer || this.#queryWidgetOne('.reviews-list__content');
    if (!contentEl) return;
    
    const newReviewsHTML = newReviews.map(review => this.renderReviewItem(review, groupingData, currentProductId)).join('');
    contentEl.insertAdjacentHTML('beforeend', newReviewsHTML);
    
    // Controleer welke nieuwe reviews een "Lees meer" knop nodig hebben
    setTimeout(() => {
      const allReviewItems = contentEl.querySelectorAll('.review-item');
      const newReviewItems = Array.from(allReviewItems).slice(-newReviews.length);
      newReviewItems.forEach(item => this.checkIfTextIsTruncated(item));
    }, 0);
  }
  
  /**
   * Geeft het totaal aantal geladen reviews terug
   * @returns {number} Aantal geladen reviews
   */
  getTotalLoadedReviews() {
    const contentEl = this.#queryWidgetOne('.reviews-list__content');
    if (!contentEl) return 0;
    
    const reviewItems = contentEl.querySelectorAll('.review-item');
    return reviewItems.length;
  }
  
  /**
   * Rendert reviews lijst
   * @param {NormalizedReviewData} data - Reviews data
   * @param {boolean} hasBreakdownFromAPI - Of er breakdown data is van de API
   */
  renderReviews(data, hasBreakdownFromAPI = false) {
    const contentEl = this.#queryWidgetOne('.reviews-list__content');
    const emptyEl = this.#queryWidgetOne('.reviews-list__empty');
    const loadingEl = this.#queryWidgetOne('.reviews-list__loading');
    
    if (!contentEl) {
      this.showError('Reviews container niet gevonden in de sectie.');
      return;
    }

    // Hide loading - BRUTE FORCE
    if (loadingEl) {
      loadingEl.style.display = 'none';
      loadingEl.setAttribute('hidden', 'true');
    }
    
    if (data.reviews.length === 0) {
      this.#showEmptyReviewsState();
      return;
    }
    
    // Show content
    contentEl.hidden = false;
    if (emptyEl) {
      emptyEl.hidden = true;
    }
    
    // Render reviews
    contentEl.innerHTML = data.reviews.map(review => this.renderReviewItem(review, data.groupingData || null, data.currentProductId || null)).join('');
    
    // Controleer welke reviews een "Lees meer" knop nodig hebben
    setTimeout(() => {
      const reviewItems = contentEl.querySelectorAll('.review-item');
      reviewItems.forEach(item => this.checkIfTextIsTruncated(item));
    }, 0);
    
    // Bereken breakdown op basis van geladen reviews alleen als er GEEN breakdown data is van de API
    if (!hasBreakdownFromAPI) {
      this.calculateAndRenderBreakdownFromReviews();
    }
    
    // Setup image modal event listeners
    this.setupImageModal();
  }
  
  /**
   * Setup event listeners voor review image modal
   */
  setupImageModal() {
    // Delegated event listener voor alle review foto knoppen
    const contentEl = this.#queryWidgetOne('.reviews-list__content');
    if (!contentEl) return;
    
    contentEl.addEventListener('click', (e) => {
      const button = e.target.closest('.review-item__photo-button');
      if (!button) return;
      
      const reviewId = button.dataset.reviewId;
      const imageIndex = parseInt(button.dataset.imageIndex);
      
      this.openImageModal(reviewId, imageIndex);
    });
  }

  /**
   * Opent de image modal voor een specifieke review
   * @param {string} reviewId - Review ID
   * @param {number} startIndex - Start afbeelding index
   */
  openImageModal(reviewId, startIndex) {
    // Vind de review data uit de DOM (data attributen) - zoek naar de photos container
    const reviewItem = this.section.querySelector(`.review-item__photos[data-review-id="${reviewId}"]`);
    if (!reviewItem) return;
    
    // Haal images_data op uit data attributen
    const imagesDataAttr = reviewItem.getAttribute('data-images');
    if (!imagesDataAttr) return;
    
    let imagesData;
    try {
      imagesData = JSON.parse(imagesDataAttr);
    } catch (e) {
      return;
    }
    
    if (!imagesData || imagesData.length === 0) {
      return;
    }
    
    // Gebruik original_url voor de modal (niet thumb_url)
    const originalImages = imagesData.map(imgData => imgData.original_url);
    
    // Vind de zoom-dialog component
    const zoomDialog = document.querySelector('#review-image-dialog');
    if (!zoomDialog) return;
    
    // Populate dialog met originele afbeeldingen
    this.populateImageDialog(zoomDialog, originalImages, startIndex);
    
    // Sla galleryContainer op zodat we het later kunnen gebruiken
    const galleryContainer = zoomDialog.querySelector('.dialog-zoomed-gallery');
    
    // Maak een fake event object voor zoomDialog.open()
    const fakeEvent = {
      preventDefault: () => {},
      target: reviewItem
    };
    
    // Open dialog
    (/** @type {any} */ (zoomDialog)).open(startIndex, fakeEvent);
    
    // IMPORTANT: De zoom-dialog.open() overschrijft refs.media met het UL element
    // We moeten onze LI elementen array TERUGzetten na het openen
    setTimeout(() => {
      // Zoek alle LI elementen in de gallery
      if (galleryContainer) {
        const galleryLI = galleryContainer.querySelectorAll('li.product-media-container');
        const liArray = Array.from(galleryLI);
        
        // Overschrijf refs.media met de LI elementen array
        if ((/** @type {any} */ (zoomDialog)).refs) {
          (/** @type {any} */ (zoomDialog)).refs.media = liArray;
        }
      }
    }, 100);
  }

  /**
   * Vult de image dialog met afbeeldingen
   * @param {HTMLElement} dialog - Dialog element
   * @param {string[]} images - Array van image URLs
   * @param {number} activeIndex - Actieve index
   */
  populateImageDialog(dialog, images, activeIndex) {
    const thumbnailsContainer = dialog.querySelector('.dialog-thumbnails-list');
    const galleryContainer = dialog.querySelector('.dialog-zoomed-gallery');
    const dialogElement = dialog.querySelector('dialog');
    
    if (!thumbnailsContainer || !galleryContainer || !dialogElement) return;
    
    // Clear bestaande content
    thumbnailsContainer.innerHTML = '';
    galleryContainer.innerHTML = '';
    
    // Initialize refs object - maar NIET overschrijven wat het Component framework al heeft gezet!
    if (!dialog.refs) dialog.refs = {};
    
    // Initialiseer media array als het nog niet bestaat
    if (!Array.isArray(dialog.refs.media)) {
      dialog.refs.media = [];
    }
    
    // Voeg thumbnails toe
    const thumbnails = [];
    images.forEach((imgSrc, index) => {
      const thumbnail = document.createElement('button');
      thumbnail.type = 'button';
      thumbnail.className = 'button button-unstyled dialog-thumbnails-list__thumbnail';
      thumbnail.setAttribute('aria-selected', index === activeIndex ? 'true' : 'false');
      thumbnail.setAttribute('aria-label', `Bekijk afbeelding ${index + 1}`);
      thumbnail.style.setProperty('--aspect-ratio', '1/1');
      thumbnail.style.setProperty('--gallery-aspect-ratio', '1/1');
      thumbnail.onclick = () => {
        // Probeer de zoom-dialog methode aan te roepen
        if (typeof (/** @type {any} */ (dialog)).handleThumbnailClick === 'function') {
          try {
            (/** @type {any} */ (dialog)).handleThumbnailClick(index);
          } catch (error) {
            // Fallback: scroll naar het juiste media item
            const media = (/** @type {any} */ (dialog)).refs?.media;
            if (media && Array.isArray(media) && media[index]) {
              media[index].scrollIntoView({ behavior: 'smooth' });
            }
          }
        } else {
          // Fallback: scroll naar het juiste media item
          const media = (/** @type {any} */ (dialog)).refs?.media;
          if (media && Array.isArray(media) && media[index]) {
            media[index].scrollIntoView({ behavior: 'smooth' });
          }
        }
      };
      thumbnail.onpointerenter = () => {
        if (typeof (/** @type {any} */ (dialog)).handleThumbnailPointerEnter === 'function') {
          (/** @type {any} */ (dialog)).handleThumbnailPointerEnter(index);
        }
      };
      thumbnail.innerHTML = `<img src="${imgSrc}" alt="Review thumbnail ${index + 1}" loading="lazy" sizes="auto, 110, (min-width: 750px) 160">`;
      thumbnailsContainer.appendChild(thumbnail);
      thumbnails.push(thumbnail);
    });
    
    // Voeg gallery items toe
    images.forEach((imgSrc, index) => {
      const li = document.createElement('li');
      li.className = 'product-media-container product-media-container--image';
      li.onclick = () => {
        if (typeof (/** @type {any} */ (dialog)).close === 'function') {
          (/** @type {any} */ (dialog)).close();
        }
      };
      li.innerHTML = `<img src="${imgSrc}" alt="Review afbeelding ${index + 1}" class="product-media__image">`;
      galleryContainer.appendChild(li);
      
      // Register met zoom-dialog refs (MEDIA MOET EEN ARRAY ZIJN!)
      if (dialog.refs && Array.isArray(dialog.refs.media)) {
        dialog.refs.media.push(li);
      }
    });
    
    
    // Update dialog ref als dat nog niet is gezet
    if (!dialog.refs.dialog) {
      dialog.refs.dialog = dialogElement;
    }
    
    // Update thumbnails ref als custom object met children array
    if (!dialog.refs.thumbnails || !dialog.refs.thumbnails.children) {
      dialog.refs.thumbnails = {
        children: thumbnails,
        querySelectorAll: (selector) => thumbnailsContainer.querySelectorAll(selector),
        appendChild: (node) => thumbnailsContainer.appendChild(node),
      };
    }
    
    // Store the media array in a local variable so we can access it in the callback
    const mediaItemsArray = dialog.refs.media;
    
    const observer = new IntersectionObserver((entries) => {
      
      // Find the most visible entry
      let mostVisibleEntry = null;
      let maxRatio = 0;
      
      entries.forEach((entry) => {
        // Gebruik de lokale media array
        if (!Array.isArray(mediaItemsArray)) {
          return;
        }
        
        // Find the index in the media array
        const index = mediaItemsArray.indexOf(/** @type {HTMLElement} */ (entry.target));
        
        if (entry.isIntersecting && entry.intersectionRatio > maxRatio) {
          maxRatio = entry.intersectionRatio;
          mostVisibleEntry = { entry, index };
        }
      });
      
      if (mostVisibleEntry && mostVisibleEntry.entry.intersectionRatio > 0.5) {
        const activeIndex = mostVisibleEntry.index;
        
        // Update actieve thumbnail
        const thumbnails = dialog.refs.thumbnails?.children;
        if (thumbnails) {
          Array.from(thumbnails).forEach((/** @type {HTMLElement} */ thumb, idx) => {
            const isActive = activeIndex === idx;
            thumb.setAttribute('aria-selected', isActive ? 'true' : 'false');
          });
        } else {
        }
      }
    }, {
      threshold: [0.5]
    });
    
    // Observe alle media items
    if (mediaItemsArray && Array.isArray(mediaItemsArray)) {
      mediaItemsArray.forEach((/** @type {HTMLElement} */ m) => {
        observer.observe(m);
      });
    }
    
    // Luister naar scroll events op de gallery container
    if (galleryContainer) {
      galleryContainer.addEventListener('scroll', () => {
        // Trigger een check van de IntersectionObserver
        observer.takeRecords();
      });
    }
    
    // Store observer voor later cleanup
    (/** @type {any} */ (dialog))._imageModalObserver = observer;
    
    // Toon dialog pas na populatie
    dialog.style.display = 'block';
  }
  
  /**
   * Haalt variant informatie op voor een review
   * @param {Object} review - Review object met product_id
   * @param {Object} groupingData - Grouping data object
   * @param {string} currentProductId - Huidige product ID
   * @returns {Object|null} Variant data met naam en URL of null
   */
  getVariantInfo(review, groupingData, currentProductId) {
    if (!groupingData || !review || !currentProductId) {
      return null;
    }
    
    // Alleen tonen als de review voor een ander product is
    if (review.product_id && review.product_id.toString() === currentProductId.toString()) {
      return null;
    }
    
    const variantData = groupingData[review.id];
    if (!variantData) {
      return null;
    }
    
    return {
      name: variantData.product_name || null,
      url: variantData.product_url || null
    };
  }

  /**
   * Controleert of een review tekst wordt afgekapt en toont/verbergt de "Lees meer" knop
   * @param {HTMLElement} reviewItem - Het review item element
   */
  checkIfTextIsTruncated(reviewItem) {
    // Controleer review content
    const contentText = reviewItem.querySelector('.review-item__content-text');
    const readMoreBtn = reviewItem.querySelector('.review-item__read-more');
    
    if (contentText && readMoreBtn) {
      const isTruncated = contentText.scrollHeight > contentText.clientHeight;
      readMoreBtn.style.display = isTruncated ? 'inline-block' : 'none';
    }
    
    // Controleer reply content
    const replyContentText = reviewItem.querySelector('.review-reply__content-text');
    const replyReadMoreBtn = reviewItem.querySelector('.review-reply__read-more');
    
    if (replyContentText && replyReadMoreBtn) {
      const isReplyTruncated = replyContentText.scrollHeight > replyContentText.clientHeight;
      replyReadMoreBtn.style.display = isReplyTruncated ? 'inline-block' : 'none';
    }
  }

  /**
   * Toggle de content van een review tussen kort en volledig
   * @param {HTMLElement} button - De "Lees meer" knop die werd geklikt
   */
  toggleReviewContent(button) {
    const reviewItem = button.closest('.review-item');
    if (!reviewItem) return;
    
    // Controleer of het een reply knop is
    const isReplyButton = button.classList.contains('review-reply__read-more');
    
    if (isReplyButton) {
      // Toggle reply content
      const replyContentText = reviewItem.querySelector('.review-reply__content-text');
      if (!replyContentText) return;
      
      const isExpanded = replyContentText.classList.contains('review-reply__content-expanded');
      
      if (isExpanded) {
        replyContentText.classList.remove('review-reply__content-expanded');
        button.textContent = 'Lees meer';
      } else {
        replyContentText.classList.add('review-reply__content-expanded');
        button.textContent = 'Lees minder';
      }
    } else {
      // Toggle review content
      const contentContainer = reviewItem.querySelector('.review-item__content');
      if (!contentContainer) return;
      
      const contentText = contentContainer.querySelector('.review-item__content-text');
      if (!contentText) return;
      
      const isExpanded = contentText.classList.contains('review-item__content-expanded');
      
      if (isExpanded) {
        contentText.classList.remove('review-item__content-expanded');
        button.textContent = 'Lees meer';
      } else {
        contentText.classList.add('review-item__content-expanded');
        button.textContent = 'Lees minder';
      }
    }
  }

  /**
   * Rendert een individuele review
   * @param {Object} review - Review data
   * @param {Object} groupingData - Grouping data voor variant informatie
   * @param {string} currentProductId - Huidige product ID
   * @returns {string} HTML string
   */
  renderReviewItem(review, groupingData = null, currentProductId = null) {
    const date = new Date(review.createdAt).toLocaleDateString('nl-NL');
    const stars = renderStarsHTML(review.score, this.starColor, this.starEmptyColor);
    
    // Haal variant informatie op
    const variantInfo = this.getVariantInfo(review, groupingData, currentProductId);
    
    let imagesHTML = '';
    if (review.images && review.images.length > 0) {
      // Sla images_data op voor modal gebruik
      const imagesDataJson = review.images_data ? JSON.stringify(review.images_data) : '';
      
      
      imagesHTML = `
        <div class="review-item__photos" data-review-id="${review.id}" data-images='${imagesDataJson}'>
          ${review.images.map((img, index) => `
            <button 
              type="button"
              class="review-item__photo-button"
              data-review-id="${review.id}"
              data-image-index="${index}"
              aria-label="Bekijk afbeelding ${index + 1} van ${review.images.length}"
            >
              <img 
                src="${img}" 
                alt="Review afbeelding ${index + 1}" 
                class="review-item__photo"
                loading="lazy"
              >
            </button>
          `).join('')}
        </div>
      `;
    }
    
    // Variant informatie HTML
    let variantHTML = '';
    if (variantInfo && variantInfo.name) {
      if (variantInfo.url) {
        variantHTML = `
          <span class="review-item__variant">
            Beoordeeld product: <a href="${this.escapeHtml(variantInfo.url)}" class="review-item__variant-link" target="_blank">${this.escapeHtml(variantInfo.name)}</a>
          </span>
        `;
      } else {
        variantHTML = `
          <span class="review-item__variant">
            Beoordeeld product: ${this.escapeHtml(variantInfo.name)}
          </span>
        `;
      }
    }

    // Comment HTML
    let commentHTML = '';
    if (review.comment && review.comment.content) {
      const commentDate = new Date(review.comment.created_at).toLocaleDateString('nl-NL');
      const commentBody = review.comment.content;
      
      commentHTML = `
        <div class="review-reply">
          <div class="review-reply__author">${this.escapeHtml(review.comment.display_name)}</div>
          <div class="review-reply__content">
            <div class="review-reply__content-text">${this.escapeHtml(commentBody)}</div>
            <a href="#" class="review-reply__read-more" data-review-id="${review.id}" style="display: none;">
              Lees meer
            </a>
          </div>
        </div>
      `;
    }

    // Bepaal review body tekst
    const reviewBody = review.body || '';
    
    // Genereer content HTML - alle reviews krijgen de functionaliteit
    const contentHTML = `
      <div class="review-item__content">
        <div class="review-item__content-text">${this.escapeHtml(reviewBody)}</div>
        <a href="#" class="review-item__read-more" data-review-id="${review.id}" style="display: none;">
          Lees meer
        </a>
      </div>
    `;
    
    return `
      <article class="review-item">
        <div class="review-item__stars">
          ${stars}
        </div>
        
        ${review.title ? `
          <h4 class="review-item__title">${this.escapeHtml(review.title)}</h4>
        ` : ''}
        
        ${contentHTML}

         <div class="review-item__meta">
          <span class="review-item__verified"><svg width="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" class="yotpo-verified-icon"><circle cx="7.5" cy="7.5" r="7.5" fill="${this.starEmptyColor}"></circle><path d="M4 7.22222L6.72269 10L11.5 5.5" stroke="white" stroke-width="1.5"></path></svg></span>
          <span class="review-item__author">${this.escapeHtml(review.author)}</span>
          <span class="review-item__date">${date}</span>
          ${variantHTML}
        </div>
        
        ${imagesHTML}
        
        ${commentHTML}
      </article>
    `;
  }
  
  /**
   * Rendert paginatie controls
   * @param {Object} pagination - Paginatie data
   */
  renderPagination(pagination) {
    const paginationEl = this.section.querySelector('.reviews-pagination');
    const prevBtn = this.section.querySelector('.reviews-pagination__prev');
    const nextBtn = this.section.querySelector('.reviews-pagination__next');
    const currentEl = this.section.querySelector('.reviews-pagination__current');
    const totalEl = this.section.querySelector('.reviews-pagination__total');
    
    if (!paginationEl) return;
    
    const totalPages = Math.ceil(pagination.total / pagination.perPage);
    
    if (totalPages <= 1) {
      paginationEl.hidden = true;
      return;
    }
    
    paginationEl.hidden = false;
    
    // Update buttons
    if (prevBtn) {
      prevBtn.disabled = pagination.page <= 1;
    }
    
    if (nextBtn) {
      nextBtn.disabled = pagination.page >= totalPages;
    }
    
    // Update info
    if (currentEl) {
      currentEl.textContent = `Pagina ${pagination.page}`;
    }
    
    if (totalEl) {
      totalEl.textContent = `van ${totalPages}`;
    }
  }
  
  /**
   * Toont loading state
   * @param {boolean} loading - Loading state
   */
  setLoading(loading) {
    this.state.isLoading = loading;

    const widget = this.#getWidgetRoot();
    if (!widget) return;

    this.#setWidgetLoadingVisible(loading);

    const contentEl = this.#queryWidgetOne('.reviews-list__content');
    const emptyEl = this.#queryWidgetOne('.reviews-list__empty');

    if (loading) {
      widget.querySelectorAll('.reviews-summary, .reviews-breakdown').forEach((el) => {
        el.hidden = true;
        el.setAttribute('aria-busy', 'true');
      });
      if (contentEl) contentEl.hidden = true;
      if (emptyEl) emptyEl.hidden = true;
    }
  }
  
  /**
   * Toont error state
   * @param {string} message - Error message
   */
  showError(message) {
    this.state.hasError = true;
    this.state.errorMessage = message;
    
    const errorEl = this.#queryWidgetOne('.reviews-list__error');
    const errorTextEl = this.#queryWidgetOne('.reviews-list__error-text');
    const loadingEl = this.#queryWidgetOne('.reviews-list__loading');
    const contentEl = this.#queryWidgetOne('.reviews-list__content');

    this.#setWidgetLoadingVisible(false);

    if (loadingEl) {
      loadingEl.style.display = 'none';
      loadingEl.setAttribute('hidden', 'true');
    }

    if (contentEl) {
      contentEl.hidden = true;
    }

    if (errorEl) {
      errorEl.hidden = false;
    }

    if (errorTextEl) {
      errorTextEl.textContent = message;
    }
  }

  /**
   * Verbergt error state
   */
  hideError() {
    this.state.hasError = false;
    this.state.errorMessage = null;

    const errorEl = this.#queryWidgetOne('.reviews-list__error');
    if (errorEl) {
      errorEl.hidden = true;
    }
  }
  
  /**
   * Controleert of debug mode actief is
   * @returns {boolean} True als debug mode actief
   */
  isDebugMode() {
    return this.section?.dataset?.debugMode === 'true';
  }
  
  /**
   * Escaped HTML om XSS te voorkomen
   * @param {string} text - Text om te escapen
   * @returns {string} Geëscapede text
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Opent de reviews dialog en laadt eerste batch reviews
   */
  async openReviewsDialog() {
    const dialogComponent = this.section.querySelector('.reviews-dialog');

    // Zonder werkende dialog (bv. dialog.js nog niet geladen) niet stilvallen:
    // val terug op het inladen van meer reviews in de widget zelf.
    if (!dialogComponent || typeof dialogComponent.showDialog !== 'function') {
      await this.loadMoreReviews();
      return;
    }

    // Reset dialog state
    this.dialogState.isOpen = true;
    this.dialogState.currentPage = 1;
    this.dialogState.hasMore = true;
    this.dialogState.isLoading = false;
    this.dialogState.infiniteScrollActive = false;
    
    // Verberg "Toon meer" knop initieel
    this.hideDialogLoadMoreButton();

    // Open dialog
    dialogComponent.showDialog();

    // Leeg dialog content en laad eerste batch reviews
    const dialogContent = dialogComponent.querySelector('.reviews-dialog__content');
    if (dialogContent) {
      dialogContent.innerHTML = '';
      await this.loadMoreInDialog();
    }

    // Setup infinite scroll
    this.setupInfiniteScroll();
    
    // Setup dialog event listeners
    this.setupDialogEventListeners();
    
    // Listen voor dialog close event
    dialogComponent.addEventListener('dialog:close', () => {
      this.dialogState.isOpen = false;
      this.dialogState.infiniteScrollActive = false;
      
      // Cleanup scroll listener
      if (this.dialogScrollHandler) {
        const scrollableContainer = dialogComponent.querySelector('.reviews-dialog__scrollable');
        if (scrollableContainer) {
          scrollableContainer.removeEventListener('scroll', this.dialogScrollHandler);
        }
        this.dialogScrollHandler = null;
      }
      
      // Verberg "Toon meer" knop
      this.hideDialogLoadMoreButton();
    });
  }

  /**
   * Setup event listeners voor dialog
   */
  setupDialogEventListeners() {
    const dialogComponent = this.section.querySelector('.reviews-dialog');
    if (!dialogComponent) return;

    // "Toon meer" knop in dialog
    const loadMoreBtn = dialogComponent.querySelector('.reviews-dialog__load-more');
    if (loadMoreBtn) {
      loadMoreBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.loadMoreInDialog();
      });
    }

    // "Lees meer" knoppen voor reviews in dialog (event delegation)
    const dialogContent = dialogComponent.querySelector('.reviews-dialog__content');
    if (dialogContent) {
      dialogContent.addEventListener('click', (e) => {
        if (e.target && e.target.classList && 
            (e.target.classList.contains('review-item__read-more') || 
             e.target.classList.contains('review-reply__read-more'))) {
          e.preventDefault();
          this.toggleReviewContent(e.target);
        }
      });
    }
  }

  /**
   * Laadt meer reviews in de dialog
   */
  async loadMoreInDialog() {
    if (this.dialogState.isLoading || !this.dialogState.hasMore) return;

    const dialogComponent = this.section.querySelector('.reviews-dialog');
    if (!dialogComponent) return;

    this.dialogState.isLoading = true;
    this.setDialogLoading(true);

    try {
      const productId = await this.resolveProductId();
      if (!productId) {
        throw new Error('Product ID niet beschikbaar');
      }

      const perPage = this.getPerPageValue();
      const options = this.buildFetchOptions(productId, perPage, this.dialogState.currentPage);

      const data = await this.providerConfig.fetchReviews(options);

      if (data.reviews.length === 0) {
        this.dialogState.hasMore = false;
        this.hideDialogLoadMoreButton();
        return;
      }

      // Voeg reviews toe aan dialog
      this.appendReviewsToDialog(data.reviews, data.groupingData || null, data.currentProductId || null);

      // Update dialog state
      this.dialogState.currentPage++;

      // Check of er nog meer reviews zijn
      const totalLoaded = this.getTotalLoadedReviewsInDialog();
      if (totalLoaded >= data.pagination.total) {
        this.dialogState.hasMore = false;
        this.hideDialogLoadMoreButton();
      } else {
        // Toon "Toon meer" knop als er nog meer reviews zijn
        this.showDialogLoadMoreButton();
      }

    } catch (error) {
      console.error('Error loading reviews in dialog:', error);
      this.dialogState.hasMore = false;
    } finally {
      this.dialogState.isLoading = false;
      this.setDialogLoading(false);
    }
  }

  /**
   * Setup infinite scroll voor dialog
   */
  setupInfiniteScroll() {
    const dialogComponent = this.section.querySelector('.reviews-dialog');
    if (!dialogComponent) return;

    const scrollableContainer = dialogComponent.querySelector('.reviews-dialog__scrollable');
    if (!scrollableContainer) return;

    // Debounced scroll handler
    let scrollTimeout;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        if (!this.dialogState.isLoading && this.dialogState.hasMore) {
          const { scrollTop, scrollHeight, clientHeight } = scrollableContainer;
          const threshold = 200; // pixels from bottom

          if (scrollTop + clientHeight >= scrollHeight - threshold) {
            this.loadMoreInDialog();
          }
        }
      }, 150);
    };

    // Remove existing listener if any
    if (this.dialogScrollHandler) {
      scrollableContainer.removeEventListener('scroll', this.dialogScrollHandler);
    }

    // Add new listener
    this.dialogScrollHandler = handleScroll;
    scrollableContainer.addEventListener('scroll', this.dialogScrollHandler);
    this.dialogState.infiniteScrollActive = true;
  }

  /**
   * Voegt reviews toe aan de dialog container
   * @param {Array} reviews - Reviews om toe te voegen
   * @param {Object} groupingData - Grouping data voor variant informatie
   * @param {string} currentProductId - Huidige product ID
   */
  appendReviewsToDialog(reviews, groupingData = null, currentProductId = null) {
    const dialogComponent = this.section.querySelector('.reviews-dialog');
    if (!dialogComponent) return;

    const dialogContent = dialogComponent.querySelector('.reviews-dialog__content');
    if (!dialogContent) return;

    // Gebruik de herbruikbare appendReviews methode
    this.appendReviews(reviews, groupingData, currentProductId, dialogContent);

    // Setup image modal voor nieuwe reviews
    this.setupImageModalForDialog();
  }

  /**
   * Setup image modal event listeners voor dialog reviews
   */
  setupImageModalForDialog() {
    const dialogComponent = this.section.querySelector('.reviews-dialog');
    if (!dialogComponent) return;

    const dialogContent = dialogComponent.querySelector('.reviews-dialog__content');
    if (!dialogContent) return;

    // Delegated event listener voor alle review foto knoppen in dialog
    dialogContent.addEventListener('click', (e) => {
      const button = e.target.closest('.review-item__photo-button');
      if (!button) return;

      const reviewId = button.dataset.reviewId;
      const imageIndex = parseInt(button.dataset.imageIndex);

      this.openImageModal(reviewId, imageIndex);
    });
  }

  /**
   * Geeft het totaal aantal geladen reviews in dialog terug
   * @returns {number} Aantal geladen reviews in dialog
   */
  getTotalLoadedReviewsInDialog() {
    const dialogComponent = this.section.querySelector('.reviews-dialog');
    if (!dialogComponent) return 0;

    const dialogContent = dialogComponent.querySelector('.reviews-dialog__content');
    if (!dialogContent) return 0;

    const reviewItems = dialogContent.querySelectorAll('.review-item');
    return reviewItems.length;
  }

  /**
   * Toont/verbergt loading state in dialog
   * @param {boolean} loading - Loading state
   */
  setDialogLoading(loading) {
    const dialogComponent = this.section.querySelector('.reviews-dialog');
    if (!dialogComponent) return;

    const loadingEl = dialogComponent.querySelector('.reviews-dialog__loading');
    if (loadingEl) {
      loadingEl.hidden = !loading;
      loadingEl.classList.toggle('hidden', !loading);
    }
  }

  /**
   * Verbergt de "Toon meer" knop in dialog
   */
  hideDialogLoadMoreButton() {
    const dialogComponent = this.section.querySelector('.reviews-dialog');
    if (!dialogComponent) return;

    const loadMoreBtn = dialogComponent.querySelector('.reviews-dialog__load-more');
    if (loadMoreBtn) {
      loadMoreBtn.style.display = 'none';
    }
  }

  /**
   * Toont de "Toon meer" knop in dialog
   */
  showDialogLoadMoreButton() {
    const dialogComponent = this.section.querySelector('.reviews-dialog');
    if (!dialogComponent) return;

    const loadMoreBtn = dialogComponent.querySelector('.reviews-dialog__load-more');
    if (loadMoreBtn) {
      loadMoreBtn.style.display = 'block';
    }
  }

  /**
   * Update show all buttons met verschillende teksten voor desktop en mobiel
   * @param {number} totalAvailable - Totaal aantal beschikbare reviews
   */
  updateShowAllButtons(totalAvailable) {
    const totalLoaded = this.getTotalLoadedReviews();
    
    // Bereken hoeveel reviews er nog geladen gaan worden
    const remainingReviews = totalAvailable - totalLoaded;
    
    // Selecteer beide versies van de show all buttons
    const desktopBtn = this.section?.querySelector('.reviews-widget__show-all--desktop');
    const mobileBtn = this.section?.querySelector('.reviews-widget__show-all--mobile');
    
    if (totalLoaded >= totalAvailable) {
      // Verberg beide buttons als alles geladen is
      if (desktopBtn) {
        desktopBtn.style.display = 'none';
      }
      if (mobileBtn) {
        mobileBtn.style.display = 'none';
      }
    } else {
      // Update desktop button met "Toon meer reviews" - toon resterend aantal
      if (desktopBtn) {
        desktopBtn.textContent = `> Toon meer reviews (${remainingReviews})`;
        desktopBtn.style.display = 'block';
        desktopBtn.style.pointerEvents = 'auto';
        desktopBtn.setAttribute('aria-busy', 'false');
      }
      
      // Update mobile button met "Toon alle reviews" - toon totaal aantal
      if (mobileBtn) {
        mobileBtn.textContent = `> Toon alle reviews (${totalAvailable})`;
        mobileBtn.style.display = 'block';
        mobileBtn.style.pointerEvents = 'auto';
        mobileBtn.setAttribute('aria-busy', 'false');
      }
    }
  }

  /**
   * Controleert of we op mobiel zijn
   * @returns {boolean} True als mobiel
   */
  isMobile() {
    return window.matchMedia('(max-width: 768px)').matches;
  }
}

function bootReviewsComponent() {
  ensureReviewsStylesheet();
  try {
    new ReviewsComponent();
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error('[hds-reviews] Initialisatie mislukt:', message);
    const loadingEl = document.querySelector(
      '.product-reviews-block .reviews-list__loading, .reviews-list__loading'
    );
    const errorEl = document.querySelector(
      '.product-reviews-block .reviews-list__error, .reviews-list__error'
    );
    const errorTextEl = document.querySelector(
      '.product-reviews-block .reviews-list__error-text, .reviews-list__error-text'
    );
    if (loadingEl instanceof HTMLElement) {
      loadingEl.style.display = 'none';
      loadingEl.setAttribute('hidden', 'true');
      loadingEl.setAttribute('aria-hidden', 'true');
    }
    if (errorEl instanceof HTMLElement) {
      errorEl.hidden = false;
    }
    if (errorTextEl instanceof HTMLElement) {
      errorTextEl.textContent = message;
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootReviewsComponent);
} else {
  bootReviewsComponent();
}

// Export voor gebruik in andere bestanden
window.ReviewsComponent = ReviewsComponent;
